import React, { useEffect, useState } from 'react';
import { getFreeCADUrl } from '../../services/docServices';
import styles from './FreeCADViewer.module.css';

function FreeCADViewer({ file }) {
  const [freecadUrl, setFreeCADUrl] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    let mounted = true;

    // Helper to normalize URL - but preserve localhost URLs in development
    const normalizeUrl = (url) => {
      if (!url) return url;
      
      // In development mode, preserve localhost URLs as they are needed for port-forwarding
      const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      
      if (isDevelopment) {
        // In development, keep localhost URLs as-is
        return url;
      } else {
        // In production, strip localhost/127.0.0.1 and port for proxy URLs
        if (url.includes('localhost') || url.includes('127.0.0.1')) {
          const fixed = url.replace(/(http|https):\/\/(localhost|127\.0\.0\.1):\d+/, '');
          return fixed;
        }
        return url;
      }
    };

    // If this is a FreeCAD virtual file, fetch the correct freecadUrl if needed
    if (file && file.type === 'freecad') {
      // Always extract server_name from file.location
      let server_name = null;
      if (file.location && typeof file.location === 'string') {
        const segments = file.location.split('/');
        if (segments.length > 0) {
          server_name = segments[segments.length - 1];
        }
      }
      if (server_name) {
        setLoading(true);
        getFreeCADUrl(server_name)
          .then((resData) => {
            if (!mounted) return;
            if (resData.RetCode !== undefined && resData.RetCode !== 0) {
              throw new Error(resData.retmsg || 'Failed to get FreeCAD server');
            }
            const dataObj = resData.data !== undefined ? resData.data : resData;
            if (!dataObj || !dataObj.url) {
              throw new Error('Invalid response format - missing FreeCAD URL');
            }
            let fixedFreeCADUrl = normalizeUrl(dataObj.url);
            setFreeCADUrl(fixedFreeCADUrl);
            setLoading(false);
          })
          .catch((err) => {
            if (mounted) {
              setError(err.message);
              setLoading(false);
            }
          });
      } else {
        setError('Could not extract server_name from file.location for FreeCAD file.');
        setLoading(false);
      }
    } else if (file && file.url) {
      let fixedFreeCADUrl = normalizeUrl(file.url);
      setFreeCADUrl(fixedFreeCADUrl);
      setLoading(false);
    } else {
      setError('No FreeCAD server URL available.');
      setLoading(false);
    }

    return () => { mounted = false; };
  }, [retryCount, file]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data && typeof event.data === 'string' && event.data.includes('websocket_error')) {
        console.error('Received WebSocket error from iframe:', event.data);
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const handleRetry = () => {
    setLoading(true);
    setError(null);
    setRetryCount(prev => prev + 1);
  };

  if (loading) {
    return <div className={styles.loading}>Starting your FreeCAD server, please wait...</div>;
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorMessage}>Error starting FreeCAD: {error}</div>
        <div>
          <button 
            onClick={handleRetry}
            className={styles.retryButton}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!freecadUrl) {
    return <div className={styles.loading}>No FreeCAD server URL available.</div>;
  }

  return (
    <iframe
      src={freecadUrl}
      className={styles.iframe}
      title="FreeCAD Interface"
      allow="clipboard-read; clipboard-write"
      sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-modals"
    />
  );
}

export default FreeCADViewer; 